/**
 * Toast — lightweight notification component
 *
 * Simple toast notification system for success/error/info messages.
 * Uses a global listener pattern to queue notifications.
 */

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useT } from "@magic-md/i18n/react";

export interface ToastOptions {
    message: string;
    type?: "success" | "error" | "info";
    durationMs?: number;
}

interface ToastRequest extends ToastOptions {
    id: string;
}

let _toastListener: ((req: ToastRequest) => void) | null = null;
let _toastId = 0;

/**
 * Show a toast notification.
 * Must have ToastHost mounted in the app to display.
 */
export function showToast(opts: ToastOptions): void {
    if (!_toastListener) {
        console.warn("[toast] No ToastHost mounted");
        return;
    }
    _toastListener({
        id: `toast-${_toastId++}`,
        message: opts.message,
        type: opts.type ?? "info",
        durationMs: opts.durationMs ?? 4000,
    });
}

/* ── ToastHost Component ── */

export function ToastHost(): React.ReactNode {
    const t = useT("editor");
    const [toasts, setToasts] = useState<ToastRequest[]>([]);
    const timerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    useEffect(() => {
        _toastListener = (req) => {
            setToasts((prev) => [...prev, req]);

            // Auto-remove after duration
            const timer = setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== req.id));
                timerRef.current.delete(req.id);
            }, req.durationMs ?? 4000);

            timerRef.current.set(req.id, timer);
        };

        return () => {
            _toastListener = null;
            timerRef.current.forEach((timer) => clearTimeout(timer));
            timerRef.current.clear();
        };
    }, []);

    const dismissToast = (id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        const timer = timerRef.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timerRef.current.delete(id);
        }
    };

    if (toasts.length === 0) {
        return null;
    }

    return (
        <div className="fixed bottom-4 right-4 flex flex-col gap-2 pointer-events-none z-50 max-w-sm">
            {toasts.map((toast) => (
                <div
                    key={toast.id}
                    className={`
                        flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg
                        pointer-events-auto animate-in slide-in-from-right-4 duration-200
                        ${
                            toast.type === "success"
                                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                                : toast.type === "error"
                                  ? "bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
                                  : "bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                        }
                    `}
                >
                    <p className="flex-1 text-sm font-medium leading-relaxed">
                        {toast.message}
                    </p>
                    <button
                        type="button"
                        onClick={() => dismissToast(toast.id)}
                        className="shrink-0 text-current/50 hover:text-current transition-colors"
                        aria-label={t("common.close")}
                    >
                        <X size={16} />
                    </button>
                </div>
            ))}
        </div>
    );
}
